// src/pages/DashboardPage.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { formatCurrency, formatDate, daysUntilColor, TENDER_STATUSES, getLabelFor } from '../utils/constants';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard').then(setStats).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;
  if (!stats) return null;

  const { by_status, by_sector, upcoming, overdue, pipeline, recent_activity } = stats;

  const activePipeline = parseFloat(pipeline?.active_pipeline || 0);
  const wonValue = parseFloat(pipeline?.won_value || 0);
  const submittedValue = parseFloat(pipeline?.submitted_value || 0);
  const total = parseInt(pipeline?.total_tenders || 0);

  const activeCount = (by_status['go'] || 0) + (by_status['bidding'] || 0) + (by_status['evaluating'] || 0);
  const wonCount = by_status['won'] || 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div style={{ fontSize:'0.85rem', color:'var(--text-muted)', marginTop:'0.2rem' }}>
            Overview of your tender pipeline
          </div>
        </div>
        <Link to="/tenders/new" className="btn btn-primary">
          <PlusIcon /> New Tender
        </Link>
      </div>

      {/* KPI Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:'1rem', marginBottom:'1.5rem' }}>
        <KpiCard label="Active Pipeline" value={formatCurrency(activePipeline)} sub={`${activeCount} tenders`} color="var(--accent)" />
        <KpiCard label="Submitted" value={formatCurrency(submittedValue)} sub={`${by_status['submitted'] || 0} tenders`} color="var(--purple)" />
        <KpiCard label="Won (Total)" value={formatCurrency(wonValue)} sub={`${wonCount} tenders`} color="var(--green)" />
        <KpiCard label="Total Tracked" value={total} sub="all time" color="var(--blue)" />
      </div>

      {/* Two-column grid */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.25rem', marginBottom:'1.25rem' }}>
        {/* Status breakdown */}
        <div className="card">
          <div style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:'1rem', letterSpacing:'0.03em', marginBottom:'1rem' }}>
            BY STATUS
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
            {TENDER_STATUSES.map(s => {
              const count = by_status[s.value] || 0;
              const pct = total > 0 ? (count / total) * 100 : 0;
              return (
                <div key={s.value} style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                  <div style={{ width:90, fontSize:'0.8rem', color:'var(--text-secondary)' }}>{s.label}</div>
                  <div style={{ flex:1, height:6, background:'var(--bg-base)', borderRadius:3, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${pct}%`, background:'var(--accent)', borderRadius:3, transition:'width 0.4s' }} />
                  </div>
                  <div style={{ width:24, textAlign:'right', fontSize:'0.8rem', fontWeight:600, color: count > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>{count}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:'1rem', letterSpacing:'0.03em', marginBottom:'1rem' }}>
            RECENT ACTIVITY
          </div>
          {recent_activity.length === 0 ? (
            <div style={{ color:'var(--text-muted)', fontSize:'0.85rem' }}>No activity yet.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
              {recent_activity.slice(0,8).map(a => (
                <div key={a.id} style={{ display:'flex', gap:'0.6rem', fontSize:'0.82rem' }}>
                  <div style={{ width:6, height:6, borderRadius:'50%', background:'var(--accent)', marginTop:6, flexShrink:0 }} />
                  <div>
                    <span style={{ color:'var(--text-secondary)' }}>{a.user_name} </span>
                    <span style={{ color:'var(--text-muted)' }}>{formatAction(a.action)} </span>
                    <Link to={`/tenders/${a.tender_id}`} style={{ color:'var(--text-primary)', fontWeight:500 }}>{a.tender_title}</Link>
                    {a.action === 'status_change' && (
                      <span style={{ color:'var(--text-muted)' }}> → <span style={{ color:'var(--accent)' }}>{getLabelFor(TENDER_STATUSES, a.new_value)}</span></span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upcoming deadlines + overdue */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.25rem' }}>
        <DeadlineCard title="UPCOMING DEADLINES" items={upcoming} type="upcoming" />
        <DeadlineCard title="OVERDUE" items={overdue} type="overdue" />
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, color }) {
  return (
    <div className="card" style={{ borderLeft:`3px solid ${color}` }}>
      <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:600, marginBottom:'0.4rem' }}>{label}</div>
      <div style={{ fontFamily:'var(--font-display)', fontSize:'1.6rem', fontWeight:800, color, letterSpacing:'0.02em', lineHeight:1 }}>{value}</div>
      <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:'0.3rem' }}>{sub}</div>
    </div>
  );
}

function DeadlineCard({ title, items, type }) {
  return (
    <div className="card">
      <div style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:'1rem', letterSpacing:'0.03em', marginBottom:'1rem', color: type === 'overdue' ? 'var(--red)' : 'var(--text-primary)' }}>
        {title}
        {type === 'overdue' && items.length > 0 && (
          <span style={{ background:'var(--red)', color:'#fff', borderRadius:10, padding:'0.1rem 0.45rem', fontSize:'0.65rem', marginLeft:'0.5rem', verticalAlign:'middle' }}>{items.length}</span>
        )}
      </div>
      {items.length === 0 ? (
        <div style={{ color:'var(--text-muted)', fontSize:'0.85rem' }}>
          {type === 'overdue' ? '✓ No overdue tenders' : 'No upcoming deadlines in 30 days'}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
          {items.map(t => (
            <div key={t.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'0.5rem' }}>
              <div>
                <Link to={`/tenders/${t.id}`} style={{ color:'var(--text-primary)', fontWeight:500, fontSize:'0.85rem' }}>{t.title}</Link>
                {t.client_name && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{t.client_name}</div>}
              </div>
              <div style={{ textAlign:'right', flexShrink:0 }}>
                <div style={{ fontSize:'0.75rem', fontWeight:700, color: type==='overdue' ? 'var(--red)' : daysUntilColor(t.days_left) }}>
                  {type === 'overdue' ? `${t.days_overdue}d overdue` : `${t.days_left}d left`}
                </div>
                <div style={{ fontSize:'0.7rem', color:'var(--text-muted)' }}>{formatDate(t.closing_date)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatAction(action) {
  const map = { created:'created', status_change:'updated status on', updated:'updated' };
  return map[action] || action;
}

function LoadingState() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:300, color:'var(--text-muted)' }}>
      Loading dashboard…
    </div>
  );
}

function PlusIcon() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
}
